"use client"

import { ArrowRight, CheckCircleSolid, ChevronLeft, ChevronRight, StarSolid } from "@medusajs/icons"
import { useMemo, useRef } from "react"
import { useProductPurchaseIntent } from "../../hooks/use-product-purchase-intent"
import type { StoreProductReviewPage, StoreProductReviewsSection } from "../../types"
import { selectPdpReviews } from "../../lib/select-pdp-reviews"
import styles from "./product-page-sections.module.css"

type FeaturedReviewsProps = {
  section: StoreProductReviewsSection
  reviews: StoreProductReviewPage
}

export default function FeaturedReviews({ section, reviews }: FeaturedReviewsProps) {
  const purchaseIntent = useProductPurchaseIntent("product-purchase-panel")
  const viewportRef = useRef<HTMLDivElement>(null)
  const cards = useMemo(
    () => selectPdpReviews(reviews.reviews, section.content.selected_review_ids, section.content.featured_limit),
    [reviews.reviews, section.content.selected_review_ids, section.content.featured_limit]
  )

  if (cards.length === 0) return null

  const ctaLabel = section.content.cta_label?.trim()
  const scrollReviews = (direction: -1 | 1) => {
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollBy({
      left: direction * Math.max(260, viewport.clientWidth * 0.72),
      behavior: "smooth",
    })
  }

  return (
    <section className={styles.ownerReviewsSection} aria-label={section.content.title}>
      <div className={styles.ownerReviewsInner}>
        <header className={styles.ownerReviewsHeader}>
          <h2 className={styles.ownerReviewsTitle}>{section.content.title}</h2>
          {section.content.subtitle ? (
            <p className={styles.ownerReviewsSubtitle}>{section.content.subtitle}</p>
          ) : null}
        </header>

        <div className={styles.ownerReviewsSliderShell}>
          <div className={styles.ownerReviewsNav} aria-label="Yorum gezintisi">
            <button type="button" className={styles.ownerReviewsNavButton} aria-label="Önceki yorum" onClick={() => scrollReviews(-1)}>
              <ChevronLeft aria-hidden="true" />
            </button>
            <button type="button" className={styles.ownerReviewsNavButton} aria-label="Sonraki yorum" onClick={() => scrollReviews(1)}>
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
          <div
            ref={viewportRef}
            className={styles.ownerReviewsViewport}
            data-testid="owner-comments-viewport"
          >
            <div className={styles.ownerReviewsTrack}>
              {cards.map((review, index) => {
                const rating = Math.min(5, Math.max(0, Math.round(review.rating)))
                const image = review.media.find((media) => media.type === "image")
                const videoPoster = review.media.find((media) => media.type === "video")
                  ?.poster_url
                const imageSrc = image?.url ?? videoPoster ?? null
                const imageAlt = image?.alt_text ?? `${review.reviewer_name} yorumu`
                const tags = review.tag ? [review.tag] : []

                return (
                  <article
                    key={review.id}
                    className={styles.ownerReviewCard}
                    data-owner-comment-index={index}
                    data-testid="featured-review-card"
                    data-has-media={imageSrc ? "true" : "false"}
                  >
                    {imageSrc ? (
                      <div className={styles.ownerReviewMedia}>
                        <img
                          className={styles.ownerReviewImage}
                          src={imageSrc}
                          alt={imageAlt}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    ) : null}

                    <div
                      className={styles.ownerReviewRating}
                      aria-label={`${rating} üzerinden 5 yıldız`}
                    >
                      {Array.from({ length: 5 }, (_, starIndex) => (
                        <StarSolid
                          key={starIndex}
                          className={
                            starIndex < rating
                              ? styles.ownerReviewStarActive
                              : styles.ownerReviewStarMuted
                          }
                          aria-hidden="true"
                        />
                      ))}
                    </div>

                    <div className={styles.ownerReviewMeta}>
                      <strong className={styles.ownerReviewName}>{review.reviewer_name}</strong>
                      {review.verified ? (
                        <span className={styles.ownerReviewVerified}>
                          <CheckCircleSolid
                            className={styles.ownerReviewVerifiedIcon}
                            aria-hidden="true"
                          />
                          <span>Doğrulanmış Sahip</span>
                        </span>
                      ) : null}
                    </div>

                    {review.body ? (
                      <p className={styles.ownerReviewComment}>{review.body}</p>
                    ) : null}

                    {tags.length ? (
                      <ul className={styles.ownerReviewTags} aria-label="Yorum etiketleri">
                        {tags.map((tag) => (
                          <li key={tag} className={styles.ownerReviewTag}>
                            {tag}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </article>
                )
              })}
            </div>
          </div>

        </div>

        {ctaLabel ? (
          <button
            type="button"
            className={styles.ownerReviewsButton}
            disabled={purchaseIntent.disabled}
            onClick={purchaseIntent.run}
          >
            <span className={styles.ownerReviewsButtonContent}>
              <span className={styles.ownerReviewsButtonLabel}>{ctaLabel}</span>
              <span className={styles.ownerReviewsButtonIcon} aria-hidden="true">
                <ArrowRight />
              </span>
            </span>
          </button>
        ) : null}
      </div>
    </section>
  )
}
